/* Form Component to Add or Edit Customer details*/

import { useEffect, useState } from "react";
import { Button, Form, Input, Select, Row, Col } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { TConfigParam, TResponseModel } from "../../../types/config";
import { eFONTFAMILY, eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { TServicePartner } from "../../../types";
import {
  AddEditConfigInfo,
  AddEditSpareProduct,
  GetConfigGroups,
  GetConfigParamDataList,
  GetSpareProductList,
} from "../../../utils/api.constant";
import { CONFIG_GROUPS, ISSUE_CATEGORY } from "../../../utils/constants";
import { requiredFieldValidator } from "../../../utils/input.validator";
import {
  CheckOutlined,
  ClearOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import CustomModal from "../../../components/modal";

type DrawerProps = {
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

type TSpareProduct = {
  id: number;
  productName: string;
  isDeleted: number;
};

/**
 * CustomerForm component is used for adding or editing customer information.
 * It provides a form with fields for customer details such as name, code,
 * address, country, state, region, district, and zipcode. The component
 * handles fetching and setting dropdown options for countries, states,
 * cities, and regions based on the selected values. It also manages form
 * submission to add or update customer records.
 *
 * Props:
 * - id: number - The ID of the customer to edit; 0 for adding a new customer.
 * - onCloseDrawer: function - Callback function to close the form drawer.
 * - onRefreshList: function - Callback function to refresh the customer list
 *   after adding or editing a customer.
 */
export default function SpareProductForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPartners, setSelectedPartners] = useState<TServicePartner[]>(
    []
  );
  const [productList, setPoductList] = useState<TSpareProduct[]>([]);
  const [selectedProduct, setSelectedProduct] =
    useState<TSpareProduct | null>();
  const [isModalVisible, setModalVisible] = useState<boolean>(false);

  const { post } = useFetch();
  const { onShowToast } = useToast();

  useEffect(() => {
    getProductList();
  }, []);

  const defaultValues: TConfigParam = {
    id: 0,
    name: "",
    description: "",
    groupName: "",
    groupId: null,
    groupUniqueId: "",
    paramUniqueId: "",
  };

  const getProductList = async () => {
    try {
      const payload = {
        data: {
          pageSize: -1,
        },
      };

      setIsLoading(true);

      const response: TResponseModel = await post(GetSpareProductList, payload);
      const { data, dataResponse } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setPoductList(data);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle on submit event
  const onFinish = async (values: any) => {
    try {
      const payload = {
        data: {
          id: selectedProduct?.id ? selectedProduct?.id : 0,
          productName: values.productName,
        },
      };
      setIsLoading(true);
      const response = await post(AddEditSpareProduct, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        props.onCloseDrawer();
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        setSelectedPartners([]);
        props.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedProduct(null);
    setModalVisible(false);
  };

  // After final confirmation from user
  const handleOk = async () => {
    try {
      const payload = {
        data: {
          id: selectedProduct?.id,
          isDeleted: 1,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditSpareProduct, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        await getProductList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
      setModalVisible(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="productName"
                label="Product Name"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("Enter product name"),
                  },
                ]}
              >
                <Input placeholder="Enter product name" />
              </Form.Item>
            </Col>
            <Col span={24}>
              {productList?.map((product: TSpareProduct) => (
                <div
                  style={{
                    // height: "2rem",
                    padding: "6px 10px",
                    display: "flex",
                    justifyContent: "space-between",
                    background: "#FFF",
                  }}
                >
                  <div>
                    <span
                      style={{
                        color: "#333",
                        fontFamily: eFONTFAMILY.MEDIUM,
                        fontSize: "14px",
                      }}
                    >
                      {product?.productName}
                    </span>
                  </div>
                  <div>
                    <EditOutlined
                      style={{
                        color: "#333",
                        fontSize: "18px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setSelectedProduct(product);
                        form.setFieldValue("productName", product.productName);
                      }}
                    />
                    <CloseCircleOutlined
                      style={{
                        color: "#333",
                        fontSize: "18px",
                        cursor: "pointer",
                        marginLeft: "5px",
                      }}
                      onClick={() => {
                        setSelectedProduct(product);
                        setModalVisible(true);
                      }}
                    />
                  </div>
                </div>
              ))}
            </Col>
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              className="btnDimensions"
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
            >
              {selectedProduct?.id ? "Update" : "Add"}
            </Button>
            <Button
              type="primary"
              className="btnDimensions"
              disabled={isLoading}
              onClick={() => {
                props.onCloseDrawer();
              }}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
      <CustomModal
        title="Confirm"
        isOpen={isModalVisible} // Ensure selectedUserId is valid
        okText="Yes"
        cusHeader={true}
        cancelText="No"
        onOk={async () => await handleOk()}
        onCancel={() => handleCancel()}
      >
        <p>
          You are about to delete {""}
          <b>{selectedProduct?.productName}</b>
        </p>
      </CustomModal>
    </FormLayout>
  );
}
